import React, { useEffect, useMemo, useState } from "react";
import { employeeService, letterTemplateService, salaryStructureService } from "../../../../services/api";
import { loadLetterTemplates } from "../../../../utils/letterTemplatesStorage";
import { DEFAULT_LETTER_TEMPLATES } from "../../../../utils/defaultLetterTemplates";
import {
  buildLetterMergeContext,
  fetchEmployeeSalaryForLetter,
  getLetterTagValueMap,
  replaceLetterTags,
  applyTagsToDocxBuffer,
  base64ToArrayBuffer,
} from "../../../../utils/letterTagEngine";
import { buildDocxBlobFromTemplateText } from "../../../../utils/letterSampleDocx";
import { getAuthJson } from "../../../../utils/authStorage";
import { DsButton } from "../../../../design-system";
import LetterPreviewSendModal from "./LetterPreviewSendModal";

const LETTER_TYPE_LABELS = {
  appointment: "Appointment",
  offer: "Offer",
  confirmation: "Confirmation",
  transfer: "Transfer",
  experience: "Experience",
  resignation: "Resignation",
  promotion: "Promotion",
  "salary-increment": "Salary Increment",
  "probation-extension": "Probation Extension",
  termination: "Termination",
  contract: "Contract",
  "contract-renewal": "Contract Renewal",
};

function formatPersonName(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  const user = value.user || {};
  return [user.name || value.name, user.lastName || value.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
}

async function hydrateEmployee(employee) {
  const empKey = employee?.id || employee?.rawId;
  let hydrated = {
    rawId: empKey,
    id: String(empKey),
    name: formatPersonName(employee?.user) || employee?.name || "",
    employeeId: employee?.empCode || employee?.employeeId || "",
    empCode: employee?.empCode || employee?.employeeId || "",
    designation: employee?.designation?.name || employee?.designation || "",
    department: employee?.department?.name || employee?.department || "",
    workLocation: employee?.branch?.name || employee?.workLocation || "",
    reportingManager: formatPersonName(employee?.reportingManager) || "",
    ctc: employee?.ctc,
    email: employee?.email || employee?.officialEmail || "",
    officialEmail: employee?.email || employee?.officialEmail || "",
    personalEmail: employee?.user?.email || employee?.personalEmail || "",
    user: employee?.user,
    dateOfJoining: employee?.dateOfJoining,
    joiningDate: employee?.dateOfJoining,
    probationPeriod: employee?.probationPeriod,
    noticePeriod: employee?.noticePeriod,
  };
  if (!empKey) return hydrated;
  try {
    const detail = await employeeService.getEmployee(empKey);
    const empDetail = detail?.employee || detail?.data || detail;
    if (!empDetail) return hydrated;
    hydrated = {
      ...hydrated,
      name:
        formatPersonName(empDetail.user) ||
        empDetail.name ||
        hydrated.name,
      employeeId: empDetail.empCode || hydrated.employeeId,
      empCode: empDetail.empCode || hydrated.empCode,
      designation: empDetail.designation?.name || hydrated.designation,
      department: empDetail.department?.name || hydrated.department,
      workLocation: empDetail.branch?.name || hydrated.workLocation,
      reportingManager:
        formatPersonName(empDetail.reportingManager) || hydrated.reportingManager,
      ctc: empDetail.ctc ?? hydrated.ctc,
      email: empDetail.email || hydrated.email,
      officialEmail: empDetail.email || hydrated.officialEmail,
      personalEmail: empDetail.user?.email || hydrated.personalEmail,
      user: empDetail.user || hydrated.user,
      dateOfJoining: empDetail.dateOfJoining || hydrated.dateOfJoining,
      joiningDate: empDetail.dateOfJoining || hydrated.joiningDate,
      probationPeriod: empDetail.probationPeriod || hydrated.probationPeriod,
      noticePeriod: empDetail.noticePeriod || hydrated.noticePeriod,
      rawId: empDetail.id || empKey,
    };
    if (
      !hydrated.reportingManager &&
      (empDetail.reportingManagerId || empDetail.reporting_manager_id)
    ) {
      const mgrId = empDetail.reportingManagerId || empDetail.reporting_manager_id;
      try {
        const mgrRes = await employeeService.getReportingEmployees();
        const list = mgrRes?.employees || mgrRes?.data || mgrRes || [];
        const mgr = (Array.isArray(list) ? list : []).find(
          (m) => String(m.id) === String(mgrId)
        );
        if (mgr) hydrated.reportingManager = formatPersonName(mgr) || mgr.empCode || "";
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* keep list fields */
  }
  return hydrated;
}

async function loadTemplateBuffer(template) {
  if (!template) return null;
  if (template.fileBase64) {
    try {
      return base64ToArrayBuffer(template.fileBase64);
    } catch {
      /* fall through */
    }
  }
  const key = template.dbId || template.id;
  if (!key || (!template.filePath && !template.uploadedFileName && !template.dbId)) {
    return null;
  }
  try {
    const blob = await letterTemplateService.downloadFile(key);
    if (!blob || blob.size === 0) return null;
    if (blob.type && blob.type.includes("application/json")) return null;
    return await blob.arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Generate a letter for a pre-selected employee (Employee list → Actions).
 */
export default function EmployeeGenerateLetterModal({
  employee,
  onClose,
  darkMode = false,
}) {
  const [letterType, setLetterType] = useState("appointment");
  const [templatesByType, setTemplatesByType] = useState(() =>
    loadLetterTemplates(DEFAULT_LETTER_TEMPLATES)
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previewPayload, setPreviewPayload] = useState(null);

  const companyData = useMemo(() => {
    const auth = getAuthJson() || {};
    return {
      name: auth.companyName || auth.company?.name || "",
      address: auth.companyAddress || auth.company?.address || "",
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await letterTemplateService.list();
        const grouped = res?.data;
        if (!grouped || cancelled) return;
        setTemplatesByType((prev) => {
          const next = { ...prev };
          Object.entries(grouped).forEach(([type, list]) => {
            if (!Array.isArray(list) || !list.length) return;
            const byId = new Map((next[type] || []).map((t) => [t.id, t]));
            list.forEach((t) => {
              const { fileBase64, ...safe } = t || {};
              byId.set(safe.id, { ...byId.get(safe.id), ...safe });
            });
            next[type] = Array.from(byId.values());
          });
          return next;
        });
      } catch {
        /* local defaults OK */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const templates = templatesByType[letterType] || [];
  useEffect(() => {
    setSelectedTemplateId(templates[0]?.id || "");
  }, [letterType, templates]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;
  const empLabel =
    employee?.user?.name ||
    employee?.name ||
    employee?.empCode ||
    "Employee";

  const handleGenerate = async () => {
    if (!selectedTemplate || !employee) {
      setError("Select a letter template first.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const hydrated = await hydrateEmployee(employee);
      const salary = await fetchEmployeeSalaryForLetter(hydrated.rawId, {
        getActiveStructure: (id) => salaryStructureService.getActiveStructure(id),
        listEmployeeStructures: (id) =>
          salaryStructureService.listEmployeeStructures(id),
      });
      const emp = buildLetterMergeContext({
        company: companyData,
        employee: hydrated,
        salaryRows: salary.rows || [],
        structure: salary.structure,
      });
      if (!emp.total_ctc && !emp.gross_salary) {
        setError(
          "No salary / CTC found for this employee. Assign a salary structure or set Annual CTC, then try again."
        );
        setBusy(false);
        return;
      }
      const tagValueMap = getLetterTagValueMap(emp, {
        preferPlainSalaryTable: true,
        forDocx: true,
        salaryRows: salary.rows || [],
      });
      const buffer = await loadTemplateBuffer(selectedTemplate);
      let outBlob;
      const safeName = `${(selectedTemplate.name || "Letter").replace(/[^\w\- ]+/g, "")}-${hydrated.employeeId || empLabel}`;
      if (buffer) {
        const filled = await applyTagsToDocxBuffer(buffer, tagValueMap);
        outBlob = new Blob([filled], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      } else {
        const merged = replaceLetterTags(
          [selectedTemplate.header, selectedTemplate.content, selectedTemplate.footer]
            .filter(Boolean)
            .join("\n\n"),
          emp,
          { preferPlainSalaryTable: true }
        );
        outBlob = await buildDocxBlobFromTemplateText({ bodyText: merged });
      }
      if (!outBlob) {
        setError("This template has no Word file or content. Upload a .docx in Letters Management.");
        setBusy(false);
        return;
      }
      const arrayBuffer = await outBlob.arrayBuffer();
      setPreviewPayload({
        employee: hydrated,
        letterName: selectedTemplate.name || LETTER_TYPE_LABELS[letterType] || "Letter",
        letterType,
        templateKey: selectedTemplate.id || selectedTemplate.templateKey || null,
        fileBaseName: safeName,
        docxBlob: outBlob,
        docxArrayBuffer: arrayBuffer,
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to generate letter.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!previewPayload && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full max-w-lg rounded-xl shadow-xl border p-5 ${
              darkMode
                ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                : "bg-white border-slate-200 text-slate-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-semibold">Generate Letter</h3>
                <p className="text-xs mt-1 opacity-70">
                  For {empLabel}
                  {employee?.empCode ? ` (${employee.empCode})` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-sm opacity-70 hover:opacity-100"
              >
                Close
              </button>
            </div>

            <label className="block text-xs font-medium mb-1">Letter type</label>
            <select
              className="w-full mb-3 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm"
              value={letterType}
              onChange={(e) => setLetterType(e.target.value)}
            >
              {Object.keys(LETTER_TYPE_LABELS).map((key) => (
                <option key={key} value={key}>
                  {LETTER_TYPE_LABELS[key]}
                </option>
              ))}
            </select>

            <label className="block text-xs font-medium mb-1">Template</label>
            <select
              className="w-full mb-3 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              {!templates.length && <option value="">No templates for this type</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id}
                  {t.uploadedFileName || t.filePath ? " (.docx)" : ""}
                </option>
              ))}
            </select>

            {error ? (
              <p className="mb-3 text-xs text-[hsl(var(--ds-danger))]">{error}</p>
            ) : (
              <p className="mb-3 text-xs opacity-70">
                After generate, you can preview the PDF, download it, or email it to the
                employee.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </DsButton>
              <DsButton
                type="button"
                onClick={handleGenerate}
                disabled={busy || !selectedTemplate}
              >
                {busy ? "Generating…" : "Generate & Preview"}
              </DsButton>
            </div>
          </div>
        </div>
      )}

      {previewPayload && (
        <LetterPreviewSendModal
          open
          darkMode={darkMode}
          employee={previewPayload.employee}
          letterName={previewPayload.letterName}
          letterType={previewPayload.letterType}
          templateKey={previewPayload.templateKey}
          fileBaseName={previewPayload.fileBaseName}
          docxBlob={previewPayload.docxBlob}
          docxArrayBuffer={previewPayload.docxArrayBuffer}
          onClose={() => {
            setPreviewPayload(null);
            onClose?.();
          }}
        />
      )}
    </>
  );
}
